import platform from '@intercom/embercom-composer/lib/platform';
import Em from 'ember';

// TODO: Explore perf impact of using an instance to memoize methods

export default Em.Object.create({
  keyCodes: {
    backspace: 8,
    tab: 9,
    enter: 13,
    shift: 16,
    alt: 18,
    capsLock: 20,
    escape: 27,
    space: 32,
    pageUp: 33,
    pageDown: 34,
    end: 35,
    home: 36,
    delete: 46,
    at: 50,
    leftCommand: 91,
    leftWindows: 91,
    rightWindows: 92,
    rightCommand: 93,
    '?': 191,
    ';': 186,
    a: 65,
    b: 66,
    c: 67,
    d: 68,
    f: 70,
    h: 72,
    i: 73,
    k: 75,
    m: 77,
    u: 85,
    v: 86,
    x: 88,
    y: 89,
    z: 90,
    e: 69,
    p: 80,
    n: 78
  },
  _keyCode(e) {
    return e.which || e.keyCode;
  },
  isKeyCode(keyEventName, e) {
    return this._keyCode(e) === this.getKeyCode(keyEventName);
  },
  getKeyCode(keyEventName) {
    return this.keyCodes[keyEventName];
  },
  isShiftOnly(e) {
    return this.isKeyCode('shift', e);
  },
  isShift(e) {
    return e.shiftKey;
  },
  isTab(e) {
    return this.isKeyCode('tab', e);
  },
  isCtrl(e) {
    return e.ctrlKey && !e.altKey;
  },
  isCmd(e) {
    return e.metaKey;
  },
  isCmdOrCtrl(e) {
    return this.isCmd(e) || this.isCtrl(e);
  },
  isCmdKey(e) {
    return platform.get('isMac') && (this.isKeyCode('leftCommand', e) || this.isKeyCode('rightCommand', e));
  },
  isAltOnly(e) {
    return this.isKeyCode('alt', e);
  },
  isAlt(e) {
    return e.altKey;
  },
  isBold(e) {
    return this.isModifierKeyOnOS(e) && this.isKeyCode('b', e);
  },
  isItalic(e) {
    //Is Italic and but doesn't clobber Chrome DevTools
    return this.isKeyCode('i', e) && (this.isCmd(e) && !this.isAlt(e) || this.isCtrl(e) && !this.isShift(e));
  },
  isAnchor(e) {
    return this.isModifierKeyOnOS(e) && this.isKeyCode('k', e);
  },
  isWindowKey(e) {
    return platform.get('isWindows') && (this.isKeyCode('leftWindows', e) || this.isKeyCode('rightWindows', e));
  },
  isModifierKeyOnOS(e) {
    if (platform.get('isLinux') || platform.get('isWindows')) {
      return this.isCtrl(e);
    }
    return this.isCmd(e) || this.isCmdKey(e);
  },
  isRemoveFocus(e) {
    return this.isEscape(e);
  },
  isEscape(e) {
    return this.isKeyCode('escape', e);
  },
  isHome(e) {
    return this.isKeyCode('home', e);
  },
  isEnd(e) {
    return this.isKeyCode('end', e);
  },
  isPageUpOrDown(e) {
    return this.isKeyCode('pageUp', e) || this.isKeyCode('pageDown', e);
  },
  isCapLocks(e) {
    return this.isKeyCode('capsLock', e);
  },
  isFunctionKey(e) {
    let key = this._keyCode(e);
    return key >= 112 && key <= 126;
  },
  isSpecialKey(e) {
    return e.which === 0 || this.isCmd(e) || this.isCtrl(e) || this.isAltOnly(e) || this.isShiftOnly(e) || this.isFunctionKey(e) || this.isCapLocks(e) || this.isPageUpOrDown(e) || this.isEnd(e) || this.isHome(e) || this.isEscape(e) || this.isTab(e);
  },
  isArrowUpOrDown(e) {
    return this.isArrowKey(e, 'up') || this.isArrowKey(e, 'down');
  },
  isNavigation(e) {
    return this.isArrowKey(e) || this.isUnixNavigation(e);
  },
  isNavigationLeft(e) {
    return this.navigationDirection(e) === 'left';
  },
  isNavigationRight(e) {
    return this.navigationDirection(e) === 'right';
  },
  isNavigationUp(e) {
    return this.navigationDirection(e) === 'up';
  },
  isNavigationDown(e) {
    return this.navigationDirection(e) === 'down';
  },
  navigationDirection(e) {
    // TODO Dry up
    let directions = {
      37: 'left',
      38: 'up',
      39: 'right',
      40: 'down'
    };
    let key = e.which || e.keyCode;
    return directions[key];
  },
  isArrowKey(e, direction) {
    let directions = {
      left: 37,
      up: 38,
      right: 39,
      down: 40
    };
    let key = e.which || e.keyCode;
    if (!direction) {
      return key >= 37 && key <= 40;
    }
    return directions[direction] === key;
  },
  isUnixNavigation(e) {
    return this.isUnixPreviousLine(e) || this.isUnixNextLine(e) || this.isUnixStartOfLine(e) || this.isUnixPreviousCharacter(e) || this.isUnixNextCharacter(e);
  },
  isUnixPreviousLine(e) {
    return this.isKeyCode('p', e) && this.isCtrlAndUnixLike(e);
  },
  isUnixNextLine(e) {
    return this.isKeyCode('n', e) && this.isCtrlAndUnixLike(e);
  },
  isUnixStartOfLine(e) {
    return this.isKeyCode('a', e) && this.isCtrlAndUnixLike(e);
  },
  isUnixPreviousCharacter(e) {
    return this.isKeyCode('b', e) && this.isCtrlAndUnixLike(e);
  },
  isUnixNextCharacter(e) {
    return this.isKeyCode('f', e) && this.isCtrlAndUnixLike(e);
  },
  isUnixDeletePreviousCharacter(e) {
    return this.isKeyCode('h', e) && this.isCtrlAndUnixLike(e);
  },
  isUnixDeleteNextCharacter(e) {
    return this.isKeyCode('d', e) && this.isCtrlAndUnixLike(e);
  },
  isCtrlAndUnixLike(e) {
    return this.isCtrl(e) && (platform.get('isMac') || platform.get('isLinux'));
  },
  isUnderline(e) {
    return this.isCtrl(e) && this.isKeyCode('u', e);
  },
  isUndo(e) {
    return this.isModifierKeyOnOS(e) && this.isKeyCode('z', e);
  },
  isRedo(e) {
    return this.isModifierKeyOnOS(e) && this.isShift(e) && this.isKeyCode('z', e) || platform.get('isWindows') && this.isCtrl(e) && this.isKeyCode('y', e);
  },
  isPaste(e) {
    return this.isModifierKeyOnOS(e) && this.isKeyCode('v', e);
  },
  isCut(e) {
    return this.isModifierKeyOnOS(e) && this.isKeyCode('x', e);
  },
  isCopy(e) {
    return this.isModifierKeyOnOS(e) && this.isKeyCode('c', e);
  },
  isSelectAll(e) {
    return this.isModifierKeyOnOS(e) && this.isKeyCode('a', e);
  },
  isDeleteToStartOfLine(e) {
    return this.isModifierKeyOnOS(e) && platform.get('isMac') && this.isKeyCode('backspace', e);
  },
  isBackspaceWord(e) {
    return this.isKeyCode('backspace', e) && (platform.get('isMac') && this.isAlt(e) || this.isCtrl(e) && (platform.get('isWindows') || platform.get('isLinux')));
  },
  isDeleteWord(e) {
    return this.isKeyCode('delete', e) && (platform.get('isMac') && this.isAlt(e) || this.isCtrl(e) && (platform.get('isWindows') || platform.get('isLinux')));
  },
  isToggleDebugOutlines(e) {
    return this.isModifierKeyOnOS(e) && this.isShift(e) && this.isAlt(e) && this.isKeyCode('?', e);
  },
  isThrowDebugException(e) {
    return this.isModifierKeyOnOS(e) && this.isShift(e) && this.isAlt(e) && this.isKeyCode('e', e);
  },
  isDirectionSwitch() {
    // temporarily disable while we figure out or keyboard shortcuts
    return false;
  },
  isSubmit(e) {
    return this.isKeyCode('enter', e) && this.isCmdOrCtrl(e);
  },
  isEnter(e) {
    return this.isKeyCode('enter', e) && !this.isCmdOrCtrl(e) || this.isKeyCode('m', e) && this.isCtrlAndUnixLike(e);
  },
  isSoftEnter(e) {
    return this.isShift(e) && this.isEnter(e);
  },
  isDelete(e) {
    return (this.isKeyCode('delete', e) || this.isUnixDeleteNextCharacter(e)) && !this.isDeleteWord(e);
  },
  isBackspace(e) {
    return (this.isKeyCode('backspace', e) || this.isUnixDeletePreviousCharacter(e)) && !this.isBackspaceWord(e);
  },
  isShiftSelection(e) {
    return this.isShift(e) && (this.isHome(e) || this.isEnd(e) || this.isPageUpOrDown(e));
  },
  isColon(e) {
    return this.isShift(e) && this.isKeyCode(';', e);
  }
});